/**
 *	Token vocabulary and the per-language rules that produce it.
 *
 *	Every language maps onto the same ten token names, because the palette is
 *	One Light / One Dark's, and has exactly that many colours — a language does
 *	not get to introduce one. `diff` is the clearest case: additions borrow the
 *	string green and removals the control-flow red.
 */

export const tokenNames = [
	'comment',
	'string',
	'global',
	'keyword',
	'break',
	'value',
	'property',
	'returnType',
	'callable',
	'bracket',
] as const;

export type TokenName = (typeof tokenNames)[number];

/**
 *	Ordered — the scanner tries each rule in turn at every position, so the
 *	first match wins. Comments and strings lead everywhere, which is what keeps
 *	a keyword inside a string from being coloured as one.
 */
export type Grammar = [TokenName, string][];

const typescript: Grammar = [
	['comment', String.raw`\/\*[\s\S]*?\*\/|\/\/[^\n]*`],
	[
		'string',
		String.raw`"(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*'|\`(?:\\.|[^\`\\])*\``,
	],
	[
		'global',
		String.raw`\b(?:import|export|from|declare|default|const|let|var|new|global)\b`,
	],
	[
		'keyword',
		String.raw`\b(?:as|async|await|case|catch|class|do|else|extends|finally|for|function|if|implements|in|instanceof|interface|of|satisfies|static|switch|try|type|typeof|while|yield)\b`,
	],
	['break', String.raw`\b(?:break|continue|return)\b`],
	[
		'value',
		String.raw`\b(?:true|false|null|undefined|unknown|never|this)\b|\b\d+(?:\.\d+)?\b`,
	],
	[
		'returnType',
		String.raw`(?<=:\s*)[A-Za-z_$][\w$.]*|(?<=\bnew\s+)[A-Z][\w$]*|(?<=[\w$]<\s*)[A-Z][\w$]*|(?<=\b(?:class|interface|type|extends|implements)\s+)[A-Z][\w$]*`,
	],
	[
		'property',
		String.raw`[A-Za-z_$][\w$]*(?=\s*:)|(?<=[\w$)\]]\??\.)[A-Za-z_$][\w$]*\b(?!\s*\()`,
	],
	['callable', String.raw`[A-Za-z_$][\w$]*(?=\s*\()|(?<=<\/?)[A-Za-z][\w$.]*`],
	['bracket', String.raw`[{}[\]()]`],
];

const css: Grammar = [
	['comment', String.raw`\/\*[\s\S]*?\*\/`],
	['global', String.raw`@[\w-]+`],
	['string', String.raw`"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'`],
	['property', String.raw`(?<=^[ \t]*|[{;]\s*)[-\w]+(?=[ \t]*:)`],
	['callable', String.raw`[-\w]+(?=\()`],
	[
		'value',
		String.raw`#[0-9a-fA-F]{3,8}\b|\b\d+(?:\.\d+)?(?:px|rem|em|ch|%|s|ms|fr|vh|vw|deg)?\b`,
	],
	[
		'keyword',
		String.raw`[.#][-\w]+|::?[-\w]+|\[[^\]\n]*\]|\b(?:html|body|a|p|ul|ol|li|pre|code|hr|table|th|td|img|svg|h[1-6])\b`,
	],
	['bracket', String.raw`[{}()]`],
];

const shell: Grammar = [
	['comment', String.raw`#[^\n]*`],
	['string', String.raw`"(?:\\.|[^"\\])*"|'[^'\n]*'`],
	['global', String.raw`\$\{?[\w]+\}?`],
	['value', String.raw`(?<=\s)--?[\w-]+`],
	['callable', String.raw`(?<=^[ \t]*)[\w.\/-]+`],
	['bracket', String.raw`[|&;<>()]`],
];

const golang: Grammar = [
	['comment', String.raw`\/\*[\s\S]*?\*\/|\/\/[^\n]*`],
	[
		'string',
		String.raw`"(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*'|\`[^\`]*\``,
	],
	[
		'global',
		String.raw`\b(?:package|import|func|type|var|const|struct|interface|map|chan)\b`,
	],
	[
		'keyword',
		String.raw`\b(?:case|default|defer|else|fallthrough|for|go|if|range|select|switch)\b`,
	],
	['break', String.raw`\b(?:break|continue|return|goto)\b`],
	['value', String.raw`\b(?:true|false|nil|iota)\b|\b\d+(?:\.\d+)?\b`],
	[
		'returnType',
		String.raw`\b(?:any|bool|byte|complex64|complex128|error|float32|float64|int|int8|int16|int32|int64|rune|string|uint|uint8|uint16|uint32|uint64|uintptr)\b|(?<=\btype\s+)\w+|[A-Z]\w*(?=\{)|(?<=[*\]]\s*(?:[a-z]\w*\.)?)[A-Z]\w*`,
	],
	['callable', String.raw`[A-Za-z_]\w*(?=\s*\()`],
	['property', String.raw`(?<=[\w$)\]]\??\.)[A-Za-z_$][\w$]*\b(?!\s*\()`],
	['bracket', String.raw`[{}[\]()]`],
];

const java: Grammar = [
	['comment', String.raw`\/\*[\s\S]*?\*\/|\/\/[^\n]*`],
	[
		'string',
		String.raw`"""[\s\S]*?"""|"(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*'`,
	],
	['global', String.raw`@\w+|\b(?:package|import|new|var)\b`],
	[
		'keyword',
		String.raw`\b(?:abstract|case|catch|class|default|do|else|enum|extends|final|finally|for|if|implements|instanceof|interface|private|protected|public|record|static|switch|synchronized|throws|try|while|yield)\b`,
	],
	['break', String.raw`\b(?:break|continue|return|throw)\b`],
	[
		'value',
		String.raw`\b(?:true|false|null|this|super)\b|\b\d+(?:\.\d+)?[LlFfDd]?\b`,
	],
	['returnType', String.raw`(?<=\bnew\s+)[A-Z][\w$]*|\b[A-Z][\w$]*\b(?!\s*\()`],
	['callable', String.raw`[A-Za-z_$][\w$]*(?=\s*\()`],
	['property', String.raw`(?<=[\w$)\]]\??\.)[A-Za-z_$][\w$]*\b(?!\s*\()`],
	['bracket', String.raw`[{}[\]()]`],
];

const diff: Grammar = [
	['comment', String.raw`^(?:diff |index |\+\+\+|---)[^\n]*`],
	['global', String.raw`^@@[^\n]*`],
	['string', String.raw`^\+[^\n]*`],
	['break', String.raw`^-[^\n]*`],
];

/**
 *	Fence info strings seen in the docs, plus the spellings a writer is likely
 *	to reach for. Anything unlisted renders as plain text rather than guessing.
 */
const grammars: Record<string, Grammar> = {
	ts: typescript,
	tsx: typescript,
	typescript: typescript,
	js: typescript,
	jsx: typescript,
	javascript: typescript,
	json: typescript,
	css: css,
	go: golang,
	golang: golang,
	java: java,
	sh: shell,
	bash: shell,
	shell: shell,
	zsh: shell,
	diff: diff,
};

export function grammarFor(language: Optional<string>): Optional<Grammar> {
	return language ? grammars[language.toLowerCase()] : undefined;
}
